import { useContext } from 'react';
import { isFedRamp, isIsolatedCloud, isolatedCloudDomain } from '@atlassian/atl-context';
import { ProductEnvironment } from '@atlassian/forge-ui-types';
import type { EgressFilteringService } from '@forge/egress';
import { useEgressPermissionService } from '../../utils/useEgressPermissionService';
import { EnvironmentContext, RendererContext } from '../../context';
import { getCDNEnvironment } from '../../utils';
import { fg } from '@atlaskit/platform-feature-flags';
import { getAtlassianImageHost as getSharedAtlassianImageHost } from '@forge/csp';

// --------------------------------------------------------------------------------------------------------------------
//
// NOTE: any changes to these hosts MUST BE applied to the Forge Monorepo -- and vice versa.
// See packages/forge-csp/src/csp/csp-injection-service.ts for the original source code.
//
// --------------------------------------------------------------------------------------------------------------------

/**
 * Media API is used to store product attachments.
 * URLs are defined here: https://developer.atlassian.com/platform/media/support/faq/#which-environment-should-i-use-
 */
const ATLASSIAN_API_GATEWAY_HOST = {
	dev: 'https://api.dev.atlassian.com',
	stg: 'https://api.stg.atlassian.com',
	prod: 'https://api.atlassian.com',
	'fedramp-stg': 'https://api.stg.atlassian-us-gov-mod.com',
	'fedramp-prod': 'https://api.atlassian-us-gov-mod.com',
};
const ATLASSIAN_ASSETS_MEDIA_HOST = {
	dev: 'https://assets-media.stg.atlassian.com',
	stg: 'https://assets-media.stg.atlassian.com',
	prod: 'https://assets-media.atlassian.com',
	'fedramp-stg': 'https://assets-media.stg.atlassian-us-gov-mod.com',
	'fedramp-prod': 'https://assets-media.atlassian-us-gov-mod.com',
};
const ATLASSIAN_MEDIA_GATEWAY_HOST = {
	dev: 'https://media.dev.atl-paas.net',
	stg: 'https://media.staging.atl-paas.net',
	prod: 'https://api.media.atlassian.com',
	'fedramp-stg': 'https://api-media.stg.atlassian-us-gov-mod.com',
	'fedramp-prod': 'https://api-media.atlassian-us-gov-mod.com',
};
const ATLASSIAN_AVATAR_HOST = {
	dev: 'avatar-management--avatars.us-west-2.staging.public.atl-paas.net',
	stg: 'avatar-management--avatars.us-west-2.staging.public.atl-paas.net',
	prod: 'avatar-management--avatars.us-west-2.prod.public.atl-paas.net',
	'fedramp-stg': 'avatar-management--avatars.us-east-1.staging.cdn.atlassian-us-gov-mod.com',
	'fedramp-prod': 'avatar-management--avatars.us-east-1.prod.cdn.atlassian-us-gov-mod.com',
};
const ATLASSIAN_EMOJIS_HOST = {
	dev: 'https://pf-emoji-service.ddev.frontend.public.atl-paas.net',
	stg: 'https://pf-emoji-service.stg-east.frontend.public.atl-paas.net',
	prod: 'https://pf-emoji-service.prod-east.frontend.public.atl-paas.net',
	'fedramp-stg': 'https://pf-emoji-service.frontend.staging.cdn.atlassian-us-gov-mod.com',
	'fedramp-prod': 'https://pf-emoji-service.frontend.cdn.atlassian-us-gov-mod.com',
};
const ATLASSIAN_TEAM_HEADER_HOST = {
	dev: 'https://teams-directory-frontend.stg-east.frontend.public.atl-paas.net/assets/gradients/',
	stg: 'https://teams-directory-frontend.stg-east.frontend.public.atl-paas.net/assets/gradients/',
	prod: 'https://teams-directory-frontend.prod-east.frontend.public.atl-paas.net/assets/gradients/',
	'fedramp-stg': 'https://teams-directory-frontend.frontend.cdn.atlassian-us-gov-mod.com/assets/',
	'fedramp-prod': 'https://teams-directory-frontend.frontend.cdn.atlassian-us-gov-mod.com/assets/',
};
const ATLASSIAN_TEAM_AVATAR_HOST = {
	dev: 'https://teams-directory-frontend.stg-east.frontend.public.atl-paas.net/assets/',
	stg: 'https://teams-directory-frontend.stg-east.frontend.public.atl-paas.net/assets/',
	prod: 'https://teams-directory-frontend.prod-east.frontend.public.atl-paas.net/assets/',
	'fedramp-stg': 'https://teams-directory-frontend.frontend.cdn.atlassian-us-gov-mod.com/assets/',
	'fedramp-prod': 'https://teams-directory-frontend.frontend.cdn.atlassian-us-gov-mod.com/assets/',
};
const ATLASSIAN_FOS_HOST = {
	dev: 'https://tdp-os.dev.services.atlassian.com',
	stg: 'https://tdp-os.stg.services.atlassian.com',
	prod: 'https://tdp-os.services.atlassian.com',
	'fedramp-stg': 'https://tdp-os.stg.services.atlassian-us-gov-mod.com',
	'fedramp-prod': 'https://tdp-os.services.atlassian-us-gov-mod.com',
};

const FOS_CDN_STANDARD_HOST = {
	dev: 'https://object-store.stg.atlassian.com',
	stg: 'https://object-store.stg.atlassian.com',
	prod: 'https://object-store.atlassian.com',
};

const getFOSHosts = (env: keyof typeof ATLASSIAN_FOS_HOST): string[] => {
	const tdpOsHosts = [
		`${ATLASSIAN_FOS_HOST[env]}/fos-eap/download/*`,
		`${ATLASSIAN_FOS_HOST[env]}/fos/app/download/*`,
		`${ATLASSIAN_FOS_HOST[env]}/fos/cdn/download/*`,
	];
	// TODO: Add FedRAMP support for FOS CDN Standard Host when determined
	if (env in FOS_CDN_STANDARD_HOST) {
		const cdnBase = FOS_CDN_STANDARD_HOST[env as keyof typeof FOS_CDN_STANDARD_HOST];
		return [...tdpOsHosts, `${cdnBase}/os/ecosystem/installation/*`];
	}
	return tdpOsHosts;
};

/**
 * Atlassian products use Wordpress as a proxy to serve avatar images in certain conditions.
 * Not allowing them by default forces developers to add the `*.wp.com` domain to the CSP image policy.
 * See: https://hello.atlassian.net/wiki/x/XJpuCgE
 */
const ATLASSIAN_IMAGES_HOSTS = {
	dev: [
		`https://${ATLASSIAN_AVATAR_HOST['dev']}`,
		`https://*.wp.com/${ATLASSIAN_AVATAR_HOST['dev']}/`,
		ATLASSIAN_API_GATEWAY_HOST['dev'],
		ATLASSIAN_MEDIA_GATEWAY_HOST['dev'],
		ATLASSIAN_EMOJIS_HOST['dev'],
		ATLASSIAN_TEAM_AVATAR_HOST['dev'],
		ATLASSIAN_TEAM_HEADER_HOST['dev'],
		ATLASSIAN_ASSETS_MEDIA_HOST['dev'],
	],
	stg: [
		`https://${ATLASSIAN_AVATAR_HOST['stg']}`,
		`https://*.wp.com/${ATLASSIAN_AVATAR_HOST['stg']}/`,
		ATLASSIAN_API_GATEWAY_HOST['stg'],
		ATLASSIAN_MEDIA_GATEWAY_HOST['stg'],
		ATLASSIAN_EMOJIS_HOST['stg'],
		ATLASSIAN_TEAM_AVATAR_HOST['stg'],
		ATLASSIAN_TEAM_HEADER_HOST['stg'],
		ATLASSIAN_ASSETS_MEDIA_HOST['stg'],
	],
	prod: [
		`https://${ATLASSIAN_AVATAR_HOST['prod']}`,
		`https://*.wp.com/${ATLASSIAN_AVATAR_HOST['prod']}/`,
		ATLASSIAN_API_GATEWAY_HOST['prod'],
		ATLASSIAN_MEDIA_GATEWAY_HOST['prod'],
		ATLASSIAN_EMOJIS_HOST['prod'],
		ATLASSIAN_TEAM_AVATAR_HOST['prod'],
		ATLASSIAN_TEAM_HEADER_HOST['prod'],
		ATLASSIAN_ASSETS_MEDIA_HOST['prod'],
	],
	'fedramp-stg': [
		`https://${ATLASSIAN_AVATAR_HOST['fedramp-stg']}`,
		`https://*.wp.com/${ATLASSIAN_AVATAR_HOST['fedramp-stg']}/`,
		ATLASSIAN_API_GATEWAY_HOST['fedramp-stg'],
		ATLASSIAN_MEDIA_GATEWAY_HOST['fedramp-stg'],
		ATLASSIAN_EMOJIS_HOST['fedramp-stg'],
		ATLASSIAN_TEAM_AVATAR_HOST['fedramp-stg'],
		ATLASSIAN_TEAM_HEADER_HOST['fedramp-stg'],
		ATLASSIAN_ASSETS_MEDIA_HOST['fedramp-stg'],
	],
	'fedramp-prod': [
		`https://${ATLASSIAN_AVATAR_HOST['fedramp-prod']}`,
		`https://*.wp.com/${ATLASSIAN_AVATAR_HOST['fedramp-prod']}/`,
		ATLASSIAN_API_GATEWAY_HOST['fedramp-prod'],
		ATLASSIAN_MEDIA_GATEWAY_HOST['fedramp-prod'],
		ATLASSIAN_EMOJIS_HOST['fedramp-prod'],
		ATLASSIAN_TEAM_AVATAR_HOST['fedramp-prod'],
		ATLASSIAN_TEAM_HEADER_HOST['fedramp-prod'],
		ATLASSIAN_ASSETS_MEDIA_HOST['fedramp-prod'],
	],
};
// --------------------------------------------------------------------------------------------------------------------

/**
 * Gravatar is still used internally to serve legacy Atlassian avatars.
 * Unsplash is integrated by default in Confluence to the header image picker functionality.
 */
const EXTERNAL_ALLOW_LISTED_IMAGES_HOSTS = [
	'https://secure.gravatar.com',
	'https://images.unsplash.com',
];

export const isLocalImage = (url: string, iframeDomainName: string | undefined): boolean => {
	if (!iframeDomainName) {
		return false;
	}

	let imageSrcUrl;
	try {
		imageSrcUrl = new URL(url);
		// Image is valid if it comes from the same domain as the iframe OR if it's being served from localhost (when using forge tunnel)
		return iframeDomainName === imageSrcUrl.host || imageSrcUrl.hostname === 'localhost';
	} catch (e) {
		return false;
	}
};

type ImageHostsEnvironment = keyof typeof ATLASSIAN_IMAGES_HOSTS;

const getImageHostsEnvironment = (environment: ProductEnvironment): ImageHostsEnvironment => {
	if (isFedRamp()) {
		if (environment === ProductEnvironment.STAGING) {
			return 'fedramp-stg';
		}
		return 'fedramp-prod';
	}
	return getCDNEnvironment(environment) as ImageHostsEnvironment;
};

const getImageHostsEnvironmentForIC = (environment: ProductEnvironment): 'ic-prod' | 'ic-stg' => {
	return environment === ProductEnvironment.PRODUCTION ? 'ic-prod' : 'ic-stg';
};

const getAtlassianImageHosts = (environment: ProductEnvironment): string[] => {
	if (fg('forge-ui-oasis-enabled')) {
		if (isIsolatedCloud()) {
			const icImageHostsEnvironment = getImageHostsEnvironmentForIC(environment);
			return getSharedAtlassianImageHost(icImageHostsEnvironment, {
				getICDomain: () => isolatedCloudDomain()!,
			});
		}
		const imageHostsEnvironment = getImageHostsEnvironment(environment);
		return getSharedAtlassianImageHost(imageHostsEnvironment);
	}
	return getAtlassianImageHostsForCommercialAndFedRamp(environment);
};

const getAtlassianImageHostsForCommercialAndFedRamp = (
	environment: ProductEnvironment,
): string[] => {
	const imageHostsEnvironment = getImageHostsEnvironment(environment);

	return [...ATLASSIAN_IMAGES_HOSTS[imageHostsEnvironment], ...getFOSHosts(imageHostsEnvironment)];
};

export const useIsValidImageUrl = (url: string): boolean => {
	const { egress, appDomainName } = useContext(RendererContext);
	const environment = useContext(EnvironmentContext);

	const allowListedImageHosts = [
		window.location.hostname,
		...getAtlassianImageHosts(environment),
		...EXTERNAL_ALLOW_LISTED_IMAGES_HOSTS,
	];

	const egressService = useEgressPermissionService('IMAGES', [
		...(egress || []),
		{
			type: 'IMAGES',
			addresses: allowListedImageHosts,
		},
	]);

	return isValidImageUrl(url, egressService, appDomainName);
};

export const isValidImageUrl = (
	url: string,
	egressService: EgressFilteringService,
	appDomainName: string | undefined,
): boolean => {
	const dataImageUrlRegex = /^data:image/;

	try {
		const isValidUrl =
			isLocalImage(url, appDomainName) ||
			dataImageUrlRegex.test(url) ||
			egressService.isValidUrl(url);

		return isValidUrl;
	} catch (e) {
		return false;
	}
};
